import React, { useState, useMemo } from 'react';
import { Box, FormControl, Grid, InputLabel, Select, MenuItem, ToggleButtonGroup, ToggleButton, Typography, Paper, Chip, useTheme } from '@mui/material';
import { TimeHeatMap, WEEKDAY_LABELS, MONTH_LABELS, HeatmapDataPoint, TimeFrame, Metric } from './components/TimeHeatMap';
import { ClusterReturnSeries } from './calculation';

const TIMEFRAME_LABELS: Record<TimeFrame, string> = {
    dayOfMonth: 'Day of Month',
    weekday: 'Weekday',
    month: 'Month',
    year: 'Year',
    quarter: 'Quarter',
    weekOfYear: 'Week of Year'
};

const METRIC_LABELS: Record<Metric, string> = {
    avgReturn: 'Avg Return',
    sharpeRatio: 'Sharpe Ratio',
    winRate: 'Win Rate',
    maxDrawdown: 'Max Drawdown',
    volatility: 'Volatility',
};

type Props = {
    clustering: {
        id: string;
        name: string;
        series: string;
    };
    clusterIds: number[];
    enabledClusters: Record<number, boolean>;
    clusterReturnSeries: ClusterReturnSeries;
}

const ClusteringTimeChart = ({
    clustering,
    clusterIds,
    enabledClusters,
    clusterReturnSeries,
}: Props) => {
    const theme = useTheme();
    const [timeFrame, setTimeFrame] = useState<TimeFrame>('month');
    const [metric, setMetric] = useState<Metric>('avgReturn');
    const [hoveredCell, setHoveredCell] = useState<HeatmapDataPoint | null>(null);

    // Utility functions
    const getPeriodKey = (timestamp: Date, timeFrameType: TimeFrame): string => {
        switch (timeFrameType) {
            case 'dayOfMonth':
                return timestamp.getDate().toString();
            case 'weekday':
                return ((timestamp.getDay() + 6) % 7).toString();
            case 'month':
                return timestamp.getMonth().toString();
            case 'year':
                return timestamp.getFullYear().toString();
            case 'quarter':
                return Math.floor(timestamp.getMonth() / 3).toString();
            case 'weekOfYear':
                const startOfYear = new Date(timestamp.getFullYear(), 0, 1);
                const weekNumber = Math.ceil(((timestamp.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
                return weekNumber.toString();
            default:
                return '0';
        }
    };

    const calculateMetric = (returns: number[], metricType: Metric): number => {
        if (returns.length === 0) return 0;

        switch (metricType) {
            case 'avgReturn':
                return returns.reduce((sum: number, r: number) => sum + r, 0) / returns.length * 100;
            case 'volatility':
                const mean = returns.reduce((sum: number, r: number) => sum + r, 0) / returns.length;
                return Math.sqrt(returns.reduce((sum: number, r: number) => sum + Math.pow(r - mean, 2), 0) / returns.length) * 100;
            case 'sharpeRatio':
                const avg = returns.reduce((sum: number, r: number) => sum + r, 0) / returns.length;
                const std = Math.sqrt(returns.reduce((sum: number, r: number) => sum + Math.pow(r - avg, 2), 0) / returns.length);
                return std === 0 ? 0 : avg / std;
            case 'winRate':
                return returns.filter((r: number) => r > 0).length / returns.length * 100;
            case 'maxDrawdown':
                let peak = 1, maxDD = 0, cumulative = 1;
                returns.forEach((r: number) => {
                    cumulative *= (1 + r);
                    if (cumulative > peak) peak = cumulative;
                    const dd = (peak - cumulative) / peak;
                    if (dd > maxDD) maxDD = dd;
                });
                return maxDD * 100;
            default:
                return 0;
        }
    };

    const getPeriodLabel = (period: string): string => {
        switch (timeFrame) {
            case 'weekday': return WEEKDAY_LABELS[parseInt(period)] || period;
            case 'month': return MONTH_LABELS[parseInt(period)] || period;
            case 'quarter': return `Q${parseInt(period) + 1}`;
            case 'dayOfMonth': return `Day ${period}`;
            case 'weekOfYear': return `Week ${period}`;
            default: return period;
        }
    };

    // Process data based on current settings
    const heatmapData = useMemo((): HeatmapDataPoint[] => {
        const parsedData: Array<{ timestamp: Date, return: number, clusterId: number }> = [];

        // Convert to individual period returns for ALL clusters
        for (let i = 0; i < clusterReturnSeries.timeseriesData.length; i++) {
            const current = clusterReturnSeries.timeseriesData[i];

            // Process all clusters
            clusterIds.forEach(clusterId => {
                const currentReturn = current.clusterReturns[clusterId];

                if (currentReturn) {
                    parsedData.push({
                        timestamp: new Date(current.timestamp),
                        return: currentReturn,
                        clusterId: clusterId
                    });
                }
            });
        }

        // Group by cluster AND time period
        const groups = new Map<string, number[]>();

        parsedData.forEach(point => {
            const periodKey = getPeriodKey(point.timestamp, timeFrame);
            const groupKey = `${point.clusterId}-${periodKey}`;

            if (!groups.has(groupKey)) {
                groups.set(groupKey, []);
            }
            groups.get(groupKey)!.push(point.return);
        });

        // Calculate metrics for each cluster-period combination
        return Array.from(groups.entries()).map(([groupKey, returns]: [string, number[]]) => {
            const [clusterIdStr, period] = groupKey.split('-');
            const clusterId = parseInt(clusterIdStr);
            const value = calculateMetric(returns, metric);

            return {
                period,
                clusterId,
                value,
                count: returns.length,
                rawReturns: returns,
                timestamps: []
            };
        });
    }, [clusterReturnSeries, clusterIds, timeFrame, metric]);

    return (
        <Box height={500}>
            <Typography variant="subtitle2" gutterBottom>
                Time Analysis – {clustering.name}
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Time Frame</InputLabel>
                            <Select value={timeFrame} label="Time Frame" onChange={(e) => setTimeFrame(e.target.value as TimeFrame)}>
                                {Object.entries(TIMEFRAME_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item>
                        <ToggleButtonGroup
                            value={metric}
                            exclusive
                            onChange={(e, newMetric) => newMetric && setMetric(newMetric)}
                            size="small"
                        >
                            {Object.entries(METRIC_LABELS).map(([key, label]) => (
                                <ToggleButton key={key} value={key} sx={{ px: 1, fontSize: '0.75rem' }}>
                                    {label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ position: 'relative' }}>
                <TimeHeatMap
                    data={heatmapData}
                    timeFrame={timeFrame}
                    metric={metric}
                    clusterIds={clusterIds}
                    onCellHover={setHoveredCell}
                />

                {hoveredCell && (
                    <Paper sx={{
                        position: 'absolute', top: 10, right: 10, p: 2,
                        backgroundColor: 'rgba(0,0,0,0.9)', color: 'white',
                        fontSize: '0.75rem', maxWidth: 300, zIndex: 10
                    }}>
                        <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
                            Cluster {hoveredCell.clusterId} - {getPeriodLabel(hoveredCell.period)} - {METRIC_LABELS[metric]}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                            Value: {['avgReturn', 'volatility', 'winRate', 'maxDrawdown'].includes(metric)
                                ? `${hoveredCell.value.toFixed(2)}%`
                                : hoveredCell.value.toFixed(3)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                            Data Points: {hoveredCell.count}
                        </Typography>
                    </Paper>
                )}
            </Box>
        </Box>
    );
};

export default ClusteringTimeChart;